import React, { Suspense, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import {
	getSmartLinksAnalyticsContextData,
	type SmartLinksAnalyticsContextData,
	type SmartLinksAnalyticsContextLocation,
} from '../../ai-answer-dialog/analytics';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { ErrorBoundary } from '../../common/ui/error-boundary';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';

type BaseColumnConfig = {
	viewAllLinkOnClick: (e: React.MouseEvent) => void;
	viewAllLinkHref: string;
	data: any[];
	component: React.ComponentType<{
		item: any;
		maxItems: number;
		onClickCallback?: (isAuxClick?: boolean) => void;
		smartLinksAnalyticsContextData?: SmartLinksAnalyticsContextData | undefined;
	}>;
	onClickCallback?: (
		position: number,
		shownAfterCardBodyExpanded: boolean,
		isAuxClick?: boolean,
	) => void;
	header: string;
};

export type ActivityColumnConfig = BaseColumnConfig & {
	smartLinksAnalyticsContextLocation: SmartLinksAnalyticsContextLocation;
};

export type UserColumnConfig = BaseColumnConfig & {
	smartLinksAnalyticsContextLocation: never;
};

export type TeamColumnConfig = BaseColumnConfig & {
	smartLinksAnalyticsContextLocation: never;
};

export type ColumnConfig = ActivityColumnConfig | UserColumnConfig | TeamColumnConfig;

type KnowledgeCardsBodyProps = {
	columnsConfig: ColumnConfig[];
	profileUrl?: string;
	footerButtonText: string;
	footerButtonAction: SainAction;
	testId?: string;
};

const CardBodyWrapper = cssMap({
	default: {
		position: 'relative',
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

const ColumnBottomPadding = cssMap({
	default: {
		paddingBottom: token('space.050'),
	},
});

const DynamicRowStyling = cssMap({
	column: {
		display: 'flex',
		flexDirection: 'column',
		rowGap: token('space.050'),
		columnGap: token('space.050'),
	},
	grid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		columnGap: token('space.200'),
		rowGap: token('space.050'),
	},
});

const EXPANDED_MAX_ITEMS_SHOWN = 6;
const MAX_ITEMS_SHOWN = 2;
const SINGLE_COLUMN_MAX_ITEMS_SHOWN = 4; // 2 rows * 2 items per row
const SINGLE_COLUMN_EXPANDED_MAX_ITEMS_SHOWN = 12; // 6 rows * 2 items per row

export const KnowledgeCardBody: React.MemoExoticComponent<
	({
		columnsConfig,
		profileUrl,
		footerButtonText,
		footerButtonAction,
		testId,
	}: KnowledgeCardsBodyProps) => React.JSX.Element
> = React.memo(
	({
		columnsConfig,
		profileUrl,
		footerButtonText,
		footerButtonAction,
		testId,
	}: KnowledgeCardsBodyProps): React.JSX.Element => {
		const { commonAttributes } = useAIAnswerContext();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { formatMessage } = useIntl();
		const [isExpanded, setExpanded] = useState(false);

		// Determine if we should show a single column layout (when only one column has data)
		const hasDataColumns = columnsConfig.filter((config) => config.data.length > 0);
		const showSingleColumn = hasDataColumns.length === 1;

		// Calculate total items across all columns for expand logic
		const totalItems = columnsConfig.reduce((sum, config) => sum + config.data.length, 0);
		const shouldShowExpandButton = showSingleColumn
			? totalItems > SINGLE_COLUMN_MAX_ITEMS_SHOWN
			: totalItems > MAX_ITEMS_SHOWN * columnsConfig.length;

		return (
			<Box xcss={CardBodyWrapper.default} testId={testId}>
				<Inline space="space.200">
					{columnsConfig.map((columnConfig, columnIndex) => {
						if (columnConfig.data.length === 0) {
							return null;
						}

						const maxItems = !isExpanded
							? showSingleColumn
								? SINGLE_COLUMN_MAX_ITEMS_SHOWN
								: MAX_ITEMS_SHOWN
							: Math.min(
									columnConfig.data.length,
									showSingleColumn
										? SINGLE_COLUMN_EXPANDED_MAX_ITEMS_SHOWN
										: EXPANDED_MAX_ITEMS_SHOWN,
								);

						return (
							<Stack
								key={columnIndex}
								grow="fill"
								space="space.050"
								xcss={ColumnBottomPadding.default}
							>
								<Box
									paddingInlineStart="space.200"
									paddingInlineEnd="space.200"
									paddingBlockEnd="space.075"
								>
									<Inline spread="space-between" alignBlock="center">
										<Heading size="xsmall" as="h3">
											{columnConfig.header}
										</Heading>
										<Link
											href={columnConfig.viewAllLinkHref}
											appearance="subtle"
											onClick={(e) => {
												columnConfig.viewAllLinkOnClick(e);
											}}
											onAuxClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
												columnConfig.viewAllLinkOnClick(e);
											}}
										>
											<Text color="color.text.subtle">
												{formatMessage(messages.knowledge_cards_view_all_link)}
											</Text>
										</Link>
									</Inline>
								</Box>
								<ErrorBoundary fallback={<></>}>
									<Suspense fallback={<></>}>
										<Provider>
											<Box
												xcss={showSingleColumn ? DynamicRowStyling.grid : DynamicRowStyling.column}
											>
												{columnConfig.data.slice(0, maxItems).map((item, index) => {
													// Calculate if this item is shown after card body expansion
													// For single column layout: items with index >= 4 are shown after expansion
													// For multi-column layout: items with index >= 2 are shown after expansion
													const shownAfterCardBodyExpanded = showSingleColumn
														? index >= SINGLE_COLUMN_MAX_ITEMS_SHOWN && isExpanded
														: index >= MAX_ITEMS_SHOWN && isExpanded;

													return (
														<columnConfig.component
															key={index}
															item={item}
															maxItems={maxItems}
															onClickCallback={
																columnConfig?.onClickCallback
																	? (isAuxClick?: boolean) =>
																			columnConfig.onClickCallback!(
																				index,
																				shownAfterCardBodyExpanded,
																				...(isAuxClick ? [isAuxClick] : []),
																			)
																	: undefined
															}
															smartLinksAnalyticsContextData={
																columnConfig.smartLinksAnalyticsContextLocation
																	? getSmartLinksAnalyticsContextData({
																			location: columnConfig.smartLinksAnalyticsContextLocation,
																			commonAttributes,
																		})
																	: undefined
															}
														/>
													);
												})}
											</Box>
										</Provider>
									</Suspense>
								</ErrorBoundary>
							</Stack>
						);
					})}
				</Inline>
				<Box>
					{shouldShowExpandButton ? (
						<Inline alignInline="center" space="space.025">
							{isExpanded ? (
								<LinkButton
									href={profileUrl || ''}
									onClick={() => {
										fireAnalyticsEvent(
											onKnowledgeCardLinkClick({
												...commonAttributes,
												location: 'footer-view-all',
											}),
										);
										fireSAINUsageEvent({ metCriteria: footerButtonAction, commonAttributes });
									}}
									onAuxClick={() => {
										if (!fg('kd_track_sain_middle_clicks')) {
											return;
										}
										fireAnalyticsEvent(
											onKnowledgeCardLinkClick({
												...commonAttributes,
												location: 'footer-view-all',
												isAuxClick: true,
											}),
										);
										fireSAINUsageEvent({ metCriteria: footerButtonAction, commonAttributes });
									}}
									appearance="subtle"
								>
										<Text color="color.text.subtlest" weight="medium">
											{footerButtonText}
										</Text>
								</LinkButton>
							) : (
								<Button
									onClick={() => {
										setExpanded((e) => !e);
										fireAnalyticsEvent(
											onKnowledgeCardLinkClick({ ...commonAttributes, location: 'show-more' }),
										);
										fireSAINUsageEvent({
											metCriteria: SainAction.EXPAND_CLICKED,
											commonAttributes,
										});
									}}
									appearance="subtle"
								>
									<Text color="color.text.subtlest" weight="medium">
										{formatMessage(messages.ai_answer_expand_button_label)}
									</Text>
								</Button>
							)}
						</Inline>
					) : null}
				</Box>
			</Box>
		);
	},
);
